import React, { useMemo } from 'react'
import { Select } from 'antd'
import type { ValueInputProps } from '@pushly/aqe'
import { useSegmentValueInputContext } from './segment-value-input-context'

/** How a prompt reads once resolved: its id, then the name it was loaded with. */
/**
 * How a prompt is named wherever it is shown.
 *
 * Exported so the collapsed row names it the same way this control's own list does. While it was
 * private the row showed the bare stored id — a number the reader had to open the editor to identify,
 * which is the one thing a summary exists to save them.
 */
export function describePrompt(id: number | string, name?: string): string {
    return name === undefined ? `#${id}` : `#${id}: ${name}`
}

/**
 * The prompt a `profile.prompt_id` condition targets.
 *
 * The condition stores a bare prompt id, so this both offers the domain's prompts and
 * resolves the stored id back to a label. The list is loaded once for the whole builder
 * and read from context rather than fetched here, so a segment with several prompt
 * conditions reads it once. Deleted prompts are in that list too — a segment saved
 * against a prompt since removed still resolves and stays selectable.
 *
 * The operator is deliberately unused: the row's own select owns `eq`/`neq`, and a copy
 * here could only disagree with the control the reader edits. The value emitted is the
 * bare number the engine and every stored segment already hold.
 */
export function PromptPickerInput({
    value,
    onChange,
    disabled,
    error,
    size,
    getPopupContainer,
}: ValueInputProps<number | null>) {
    const { prompts, promptsLoading } = useSegmentValueInputContext()

    const selected = typeof value === 'number' ? value : undefined

    const options = useMemo(() => {
        const resolved = prompts.map((prompt) => ({ value: prompt.id, label: describePrompt(prompt.id, prompt.name) }))

        // A stored id the loaded list does not hold still has to render rather than
        // blank the row — otherwise the next save drops what the condition targeted.
        // With deleted prompts included this is the rare stray, shown by id alone
        // because its name never arrived.
        if (selected !== undefined && !prompts.some((prompt) => prompt.id === selected)) {
            return [{ value: selected, label: describePrompt(selected) }, ...resolved]
        }

        return resolved
    }, [prompts, selected])

    return (
        <div className="segment-prompt-picker">
            <Select
                className="segment-prompt-picker__select"
                style={{ width: '100%' }}
                showSearch={true}
                optionFilterProp="label"
                placeholder="Select a Prompt"
                loading={promptsLoading}
                value={selected}
                options={options}
                onChange={(next: number) => onChange(next)}
                disabled={disabled}
                size={size}
                status={error ? 'error' : undefined}
                getPopupContainer={getPopupContainer}
            />
        </div>
    )
}
